/**
 * Device-based breakpoint configuration
 * Based on device types rather than specific models
 */

export const breakpoints = {
  // Phones (small screens)
  phone: {
    max: 767, // max-width: 767px
    query: '(max-width: 767px)',
  },
  
  // Tablets/iPads (mid size)
  tablet: {
    min: 768, // min-width: 768px
    max: 1023, // max-width: 1023px
    query: '(min-width: 768px) and (max-width: 1023px)',
    minQuery: '(min-width: 768px)',
    maxQuery: '(max-width: 1023px)',
  },
  
  // Laptop screens
  laptop: {
    min: 1024, // min-width: 1024px
    max: 1919, // max-width: 1919px
    query: '(min-width: 1024px) and (max-width: 1919px)',
    minQuery: '(min-width: 1024px)',
    maxQuery: '(max-width: 1919px)',
  },
  
  // Desktop monitors (large)
  desktop: {
    min: 1920, // min-width: 1920px
    query: '(min-width: 1920px)',
  },
};

/**
 * CSS custom properties for breakpoints (to use in CSS files)
 * Usage: @media (max-width: var(--breakpoint-phone-max)) { ... }
 */
export const cssBreakpoints = `
  :root {
    --breakpoint-phone-max: 767px;
    --breakpoint-tablet-min: 768px;
    --breakpoint-tablet-max: 1023px;
    --breakpoint-laptop-min: 1024px;
    --breakpoint-laptop-max: 1919px;
    --breakpoint-desktop-min: 1920px;
  }
`;

/**
 * Helper function to check current screen size category
 * @returns {string} 'phone' | 'tablet' | 'laptop' | 'desktop'
 */
export const getDeviceType = () => {
  if (typeof window === 'undefined') return 'desktop';
  
  const width = window.innerWidth;
  
  if (width < breakpoints.tablet.min) {
    return 'phone';
  } else if (width >= breakpoints.tablet.min && width <= breakpoints.tablet.max) {
    return 'tablet';
  } else if (width >= breakpoints.laptop.min && width <= breakpoints.laptop.max) {
    return 'laptop';
  } else {
    return 'desktop';
  }
};

/**
 * Helper function to check if current device is mobile (phone)
 * @returns {boolean}
 */
export const isMobileDevice = () => {
  return getDeviceType() === 'phone';
};

/**
 * Helper function to check if current device is tablet
 * @returns {boolean}
 */
export const isTabletDevice = () => {
  return getDeviceType() === 'tablet';
};

/**
 * Helper function to check if current device is laptop
 * @returns {boolean}
 */
export const isLaptopDevice = () => {
  return getDeviceType() === 'laptop';
};

/**
 * Helper function to check if current device is desktop
 * @returns {boolean}
 */
export const isDesktopDevice = () => {
  return getDeviceType() === 'desktop';
};

/**
 * Media query matchers for use in JavaScript
 */
export const mediaQueries = {
  phone: () => window.matchMedia(breakpoints.phone.query),
  tablet: () => window.matchMedia(breakpoints.tablet.query),
  tabletAndUp: () => window.matchMedia(breakpoints.tablet.minQuery),
  laptop: () => window.matchMedia(breakpoints.laptop.query),
  laptopAndUp: () => window.matchMedia(breakpoints.laptop.minQuery),
  desktop: () => window.matchMedia(breakpoints.desktop.query),
};






